export function isFunction(obj: any): obj is Function {
  return typeof obj === 'function';
}

export function promiser<T = any>(): {
  resolve: (result?: T) => void;
  reject: (error: any) => void;
  promise: Promise<T>;
} {
  let resolve: (result?: T) => void;
  let reject: (error: any) => void;
  const promise: Promise<T> = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { resolve, reject, promise };
}

/**
 * Handles the provides function after promise resolved or rejected.
 * The returned value is the same promise provides, it is not the chained promise running the function provided.
 * i.e. The following expression `promiseFinally(myPromise, () => {}) === myPromise` is true.
 */
export function promiseFinally<T>(promise: Promise<T>, fn: (error?: any, value?: T) => void): Promise<T> {
  promise.then((v) => fn(undefined, v)).catch(fn);
  return promise;
}

export function removeFromArray<T = any>(arr: T[], value: T): boolean;
export function removeFromArray<T = any>(arr: T[], values: T[]): boolean[];
export function removeFromArray<T = any>(arr: T[], value: T | T[]): boolean | boolean[] {
  if (Array.isArray(value)) {
    return value.map((v) => removeFromArray(arr, v));
  } else {
    const idx = arr.indexOf(value);
    if (idx > -1) {
      arr.splice(idx, 1);
      return true;
    } else {
      return false;
    }
  }
}

export function arrayHasItems(array: any[]): boolean {
  return Array.isArray(array) && array.length > 0;
}

/** Returns a number between minResult and maxResult (linear function).
 * If the sourceValue is <= minBoudary the function will return minResult
 * If the sourceValue is >= maxBoundary the function will return maxResult.
 *   */
export function interpolateInRange(sourceValue, minBoudary, maxBonudary, minResult, maxResult) {
  // Clamp the value within the range [min, max]
  sourceValue = Math.max(minBoudary, Math.min(maxBonudary, sourceValue));
  // Calculate the interpolation factor
  const factor = (sourceValue - minBoudary) / (maxBonudary - minBoudary);
  // Perform linear interpolatio
  const result = minResult + factor * (maxResult - minResult);
  return result;
}

export function removeNullValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => this.removeNullValues(item));
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null) {
        cleaned[key] = this.removeNullValues(value);
      }
    }
    return cleaned;
  }

  return obj;
}

/**
 * Fast deep equality comparison for any nested objects/arrays.
 * @param a - First value to compare
 * @param b - Second value to compare
 * @param treatNullAsUndefined - Whether null and undefined are considered equal (default: true)
 * @returns true if values are deeply equal
 */
export function fastDeepEqual(a: any, b: any, treatNullAsUndefined = true): boolean {
  if (a === b) return true;

  // Normalize null and undefined if enabled
  if (treatNullAsUndefined && (a === null || a === undefined) && (b === null || b === undefined)) return true;

  if (typeof a !== typeof b) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!fastDeepEqual(a[i], b[i], treatNullAsUndefined)) return false;
    }
    return true;
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      const valA = a[key];
      const valB = b[key];
      if (!fastDeepEqual(valA, valB, treatNullAsUndefined)) return false;
    }
    return true;
  }

  return false;
}
